import React, { useEffect, useState } from 'react';

import { lowerCase } from 'lodash';

import { Icon } from '../../../UI';
import { StrategyProgressBar } from '../ProgressBar/StrategyProgressBar';
import PriceTargetsAccordion from './PriceTargetsAccordion';
import PriceTargetsListItemSingle from '../SingleColumnView';

import { computePercentage, getSymbol } from '../../../../helpers/functions';

import { IStrategyAsset } from '../../../../types/model';
import { Currency } from '../../../../types/common';
import { CustomNumber } from '../../../CustomNumber/CustomNumber';
import { ProfitLoss } from '../../../ProfitLoss';
import { useModalContext } from '../../../../context/Modal';
import { AdvancedSettings } from '../../Portfolio/AdvancedSettings';
import { Asset } from '../../../Asset/Asset';

const PriceTargetsListItem = ({
  strategyAsset,
  currency,
  width,
  setAssetTargetDetails,
}: {
  strategyAsset: IStrategyAsset;
  currency: Currency['code'];
  width: number;
  setAssetTargetDetails: React.Dispatch<
    React.SetStateAction<JSX.Element | undefined>
  >;
}) => {
  const { dispatch: dispatchModal } = useModalContext();
  const { asset, entryPrice, strategyAssetTargets, id, connectionType } =
    strategyAsset;
  const { history, logo, name, symbol, type, exchange } = asset;
  const lastPrice = history[0][currency.toLocaleLowerCase()];

  const [isVisible, setIsVisible] = useState(false);

  const percentage = computePercentage(entryPrice!, lastPrice, 100);

  useEffect(() => {
    setIsVisible(false);
  }, [asset]);

  if (width === 1) {
    return (
      <PriceTargetsListItemSingle
        assetData={strategyAsset}
        currency={currency}
        setAssetTargetDetails={setAssetTargetDetails}
      />
    );
  }

  const assetHasTargets = strategyAsset.strategyAssetTargets.items.length > 0;

  return (
    <>
      <div
        className={`
          ${isVisible && 'border-t border-l border-r border-grey-50'}
        `}
      >
        <div
          className={`
          grid
          grid-cols-5
          ${!isVisible && 'border-b border-grey-10 dark:border-grey-80'}
        `}
        >
          <div
            className={`
              flex
              sticky
              w-full
              h-16
              left-0
              px-3
              bg-transparent
              ${width === 2 && 'border-r border-grey-10'}
            `}
          >
            <Asset
              name={name}
              type={type || ''}
              logo={logo}
              symbol={symbol}
              connectionType={connectionType}
              id={id}
              exchange={exchange}
            />
          </div>
          <CustomNumber
            className="ml-2"
            value={entryPrice}
            suffix={getSymbol(currency)}
          />

          <div className="flex flex-col my-auto">
            <CustomNumber value={lastPrice} />
            <ProfitLoss
              valueClassName="w-32"
              isRow
              percentage={percentage}
              performancePosition="value"
              prefixPercentage="("
              suffixPercentage="%)"
              symbol={getSymbol(currency)}
              value={lastPrice - entryPrice}
            />
          </div>

          <div className="flex col-span-2 items-center pr-4">
            <StrategyProgressBar
              currency={currency}
              entryPrice={entryPrice}
              lastPrice={history[0][lowerCase(currency)]}
              markers={strategyAssetTargets!.items}
              className="w-full ml-5"
            />
            {width !== 1 && (
              <div
                className="flex items-center ml-2 cursor-pointer"
                onClick={() => {
                  if (assetHasTargets) {
                    setIsVisible((prev) => !prev);
                  } else {
                    // Asset doesn't have targets, show advanced settings to add
                    dispatchModal({
                      element: (
                        <AdvancedSettings
                          asset={strategyAsset}
                          currency={currency}
                          onlyTargets={true}
                        />
                      ),
                      handleMerlinLogo: true,
                      isFullScreenElement: false,
                      isClosable: true,
                      title: 'Advanced settings',
                    });
                  }
                }}
              >
                <Icon
                  svgName={
                    assetHasTargets
                      ? isVisible
                        ? 'arrowUp'
                        : 'arrowDown'
                      : 'plus'
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {width !== 1 && isVisible && (
        <PriceTargetsAccordion
          assetData={strategyAsset}
          className={`
            grid
            grid-cols-8
            divide-y-1
            divide-grey-10
            bg-white
            dark:bg-black
            border-b
            border-l
            border-r
            border-grey-50
          `}
          currency={currency}
          width={width}
        />
      )}
    </>
  );
};

export default PriceTargetsListItem;
